import { type MbscResource } from '@mobiscroll/react';

import { clusterzuordnung, ExperteFromResourcesRouter } from '../../../dtos';
import {
  CalendarExperteResource,
  CalendarGruppenraumResource,
  calendarResourceTypes,
  CalendarSeminarraumResource,
  CalendarStandortResource,
  isExperteResource,
} from '../ResourceCalendar';
import { AbstractCalendarResource } from '../ResourceCalendar/helperClasses/CalendarResource';
import { LocationResource } from '../ResourceCalendar/helperClasses/CalendarResource/LocationResource';
import { type content } from './VeranstaltungRessourcenCalendar/VeranstaltungRessourcenCalendar.content';

export type StandortResourceGroup = Omit<MbscResource, 'id' | 'name' | 'children'> & {
  id: typeof calendarResourceTypes.STANDORT;
  name: typeof content.calenderResourceGroupLabels.STANDORT;
  children: CalendarStandortResource[];
};

export type SeminarraumResourceGroup = Omit<MbscResource, 'id' | 'name' | 'children'> & {
  id: typeof calendarResourceTypes.SEMINARRAUM;
  name: typeof content.calenderResourceGroupLabels.SEMINARRAUM;
  children: LocationResource<CalendarSeminarraumResource>[];
};

export type GruppenraumResourceGroup = Omit<MbscResource, 'id' | 'name' | 'children'> & {
  id: typeof calendarResourceTypes.GRUPPENRAUM;
  name: typeof content.calenderResourceGroupLabels.GRUPPENRAUM;
  children: LocationResource<CalendarGruppenraumResource>[];
};

export type ExperteResourceGroup = Omit<MbscResource, 'id' | 'name' | 'children'> & {
  id: typeof calendarResourceTypes.EXPERTE;
  name: typeof content.calenderResourceGroupLabels.EXPERTE;
  children: (HaupttrainerResourceGroup | NebentrainerResourceGroup | ErsatztrainerResourceGroup)[];
};

type TrainerBaseResourceGroup = Omit<MbscResource, 'id' | 'name' | 'children'> & {
  name: string;
  background: string;
  color: string;
};

type HaupttrainerResource = CalendarExperteResource & {
  experte: ExperteFromResourcesRouter & { clusterzuordnung: typeof clusterzuordnung.HAUPTTRAINER };
};
export const isHaupttrainerResource = (resource: AbstractCalendarResource): resource is HaupttrainerResource =>
  isExperteResource(resource) && resource.experte.clusterzuordnung === clusterzuordnung.HAUPTTRAINER;

export type HaupttrainerResourceGroup = TrainerBaseResourceGroup & {
  id: typeof clusterzuordnung.HAUPTTRAINER;
  children: HaupttrainerResource[];
};

type NebentrainerResource = CalendarExperteResource & {
  experte: ExperteFromResourcesRouter & { clusterzuordnung: typeof clusterzuordnung.NEBENTRAINER };
};
export const isNebentrainerResource = (resource: AbstractCalendarResource): resource is NebentrainerResource =>
  isExperteResource(resource) && resource.experte.clusterzuordnung === clusterzuordnung.NEBENTRAINER;

export type NebentrainerResourceGroup = TrainerBaseResourceGroup & {
  id: typeof clusterzuordnung.NEBENTRAINER;
  children: NebentrainerResource[];
};

type ErsatztrainerResource = CalendarExperteResource & {
  experte: ExperteFromResourcesRouter & { clusterzuordnung: typeof clusterzuordnung.ERSATZTRAINER };
};
export const isErsatztrainerResource = (resource: AbstractCalendarResource): resource is ErsatztrainerResource =>
  isExperteResource(resource) && resource.experte.clusterzuordnung === clusterzuordnung.ERSATZTRAINER;

export type ErsatztrainerResourceGroup = TrainerBaseResourceGroup & {
  id: typeof clusterzuordnung.ERSATZTRAINER;
  children: ErsatztrainerResource[];
};

export type CollapseInfo = {
  seminarraum: boolean;
  gruppenraum: boolean;
  standort: boolean;
  experte: boolean;
  haupttrainer: boolean;
  nebentrainer: boolean;
  ersatztrainer: boolean;
};
